import { forwardRef, useEffect, useRef, useState } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import {
  Gift,
  Globe,
  Smartphone,
  Crown,
  Sparkles,
  Users,
  ScanLine,
  Landmark,
  Watch,
  Tablet,
  Bitcoin,
  Monitor,
  Mic,
  LayoutGrid,
  type LucideIcon,
} from 'lucide-react'

gsap.registerPlugin(ScrollTrigger)

type Milestone = {
  when: string
  title: string
  desc: string
  status: 'now' | 'next' | 'later'
  Icon: LucideIcon
}

const ROADMAP: Milestone[] = [
  { when: 'v1.0', title: 'Free for everyone', desc: 'All features unlocked. No paywall, no ads.', status: 'now', Icon: Gift },
  { when: 'v1.0', title: 'Apple Pay auto-import', desc: 'Tap to pay → transaction lands in Ruvi via Shortcuts.', status: 'now', Icon: Smartphone },
  { when: 'v1.0', title: '35+ currencies', desc: 'Multi-currency accounts with live ECB rates.', status: 'now', Icon: Globe },
  { when: 'v1.1', title: 'AI insights', desc: 'Smart categorization and a monthly report.', status: 'next', Icon: Sparkles },
  { when: 'v1.1', title: 'Shared accounts', desc: 'Family workspace via private CloudKit zones.', status: 'next', Icon: Users },
  { when: 'v1.1', title: 'Receipt OCR', desc: 'Snap a receipt, get a transaction.', status: 'next', Icon: ScanLine },
  { when: 'v1.1', title: 'Pro tier', desc: 'Optional subscription for premium add-ons.', status: 'next', Icon: Crown },
  { when: 'v1.2', title: 'Bank sync', desc: 'Open banking in EU and Ukraine.', status: 'later', Icon: Landmark },
  { when: 'v1.2', title: 'Apple Watch', desc: 'Quick add from your wrist.', status: 'later', Icon: Watch },
  { when: 'v1.2', title: 'iPad app', desc: 'Optimised for the bigger canvas.', status: 'later', Icon: Tablet },
  { when: 'v1.3', title: 'Crypto portfolio', desc: 'Live prices, wallet imports.', status: 'later', Icon: Bitcoin },
  { when: 'v1.3', title: 'macOS app', desc: 'Same app, bigger screen.', status: 'later', Icon: Monitor },
  { when: 'v1.3', title: 'Siri & App Intents', desc: 'Voice-add transactions hands-free.', status: 'later', Icon: Mic },
  { when: 'v1.4', title: 'Widgets + Lock Screen', desc: 'Daily limit at a glance.', status: 'later', Icon: LayoutGrid },
]

const STATUS_BADGE: Record<Milestone['status'], { label: string; className: string }> = {
  now: { label: 'Now', className: 'text-bg-0 bg-[var(--accent)]' },
  next: { label: 'Next', className: 'text-ink-1 bg-white/10 border border-white/15' },
  later: { label: 'Later', className: 'text-ink-2 bg-white/[0.04] border border-white/10' },
}

export function Roadmap() {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const wrapper = wrapperRef.current
    const track = trackRef.current
    if (!wrapper || !track) return

    const ctx = gsap.context(() => {
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (!isDesktop || reduceMotion) return

      // How far the track has to translate left so the last card lands in view.
      const getDistance = () => Math.max(0, track.scrollWidth - window.innerWidth + 128)

      // Pin the whole section. The flex column inside (title + track) stays
      // visually fixed; only the track translates horizontally as the user
      // scrolls. Result: the heading is always visible while cards slide past.
      const horizontal = gsap.to(track, {
        x: () => -getDistance(),
        ease: 'none',
        scrollTrigger: {
          trigger: wrapper,
          pin: true,
          scrub: 1,
          end: () => `+=${getDistance()}`,
          invalidateOnRefresh: true,
          anticipatePin: 1,
        },
      })

      gsap.utils.toArray<HTMLElement>('.rm-card').forEach((card) => {
        const inner = card.querySelectorAll<HTMLElement>('.rm-card-inner > *')
        const fill = card.querySelector<HTMLElement>('.rm-fill')

        gsap.from(card, {
          opacity: 0,
          y: 60,
          scale: 0.96,
          ease: 'expo.out',
          duration: 0.9,
          scrollTrigger: {
            trigger: card,
            containerAnimation: horizontal,
            start: 'left 90%',
            end: 'left 50%',
            toggleActions: 'play none none reverse',
          },
        })

        if (fill) {
          gsap.fromTo(
            fill,
            { opacity: 0 },
            {
              opacity: 1,
              ease: 'none',
              scrollTrigger: {
                trigger: card,
                containerAnimation: horizontal,
                start: 'left 85%',
                end: 'left 30%',
                scrub: true,
              },
            }
          )
        }

        gsap.from(inner, {
          opacity: 0,
          y: 14,
          duration: 0.6,
          ease: 'power3.out',
          stagger: 0.07,
          scrollTrigger: {
            trigger: card,
            containerAnimation: horizontal,
            start: 'left 80%',
            toggleActions: 'play none none reverse',
          },
        })
      })

      // Eyebrow + title reveal — fires when the section is solidly entering
      // view (top 60%, not 75%) so the user actually sees it animate in,
      // not "already there by the time I scroll past".
      gsap.from('.rm-eyebrow', {
        opacity: 0,
        y: 20,
        duration: 0.7,
        ease: 'expo.out',
        scrollTrigger: {
          trigger: wrapper,
          start: 'top 60%',
          toggleActions: 'play none none reverse',
        },
      })
      // Letter-by-letter stagger for "Roadmap" — each glyph rises from
      // below with a spring-y feel. Much more noticeable than a single
      // opacity fade, which the old version was getting lost in.
      gsap.from('.rm-letter', {
        opacity: 0,
        yPercent: 110,
        rotate: 6,
        stagger: 0.06,
        duration: 0.9,
        ease: 'expo.out',
        scrollTrigger: {
          trigger: wrapper,
          start: 'top 60%',
          toggleActions: 'play none none reverse',
        },
      })
    }, wrapper)
    return () => ctx.revert()
  }, [])

  return (
    <section id="roadmap" ref={wrapperRef} className="relative" aria-label="Roadmap">
      {/* Desktop: title + horizontal track pinned together as one centered group */}
      <div className="hidden lg:block h-[100vh] overflow-hidden">
        <div className="h-full flex flex-col justify-center pl-6 lg:pl-16 pr-0">
          <div className="rm-eyebrow eyebrow shrink-0 mb-3 lg:mb-4">What's next</div>
          <h2
            id="roadmap-heading"
            className="rm-title font-display font-semibold text-ink-1 shrink-0 overflow-hidden"
            style={{
              fontSize: 'clamp(56px, 8vw, 128px)',
              letterSpacing: '-0.04em',
              lineHeight: 1.05,
              paddingBottom: '0.05em',
            }}
            aria-label="Roadmap"
          >
            <span aria-hidden className="inline-flex">
              {Array.from('Roadmap').map((c, idx) => (
                <span key={idx} className="rm-letter inline-block" style={{ willChange: 'transform' }}>
                  {c}
                </span>
              ))}
            </span>
          </h2>

          <div className="shrink-0 mt-10 lg:mt-14">
            <div ref={trackRef} className="flex gap-4 will-change-transform">
              {ROADMAP.map((m, i) => (
                <RoadmapCard key={i} milestone={m} index={i} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile/tablet: Stories-style horizontal swipe */}
      <div className="lg:hidden">
        <RoadmapStories milestones={ROADMAP} />
      </div>
    </section>
  )
}

// ============================================================================
// Mobile Roadmap — Instagram-Stories-style horizontal swipe.
// One card per viewport, scroll-snap, progress bars per version up top.
// Active card tracked via IntersectionObserver with root=scroller.
// ============================================================================

function RoadmapStories({ milestones }: { milestones: Milestone[] }) {
  const [activeIdx, setActiveIdx] = useState(0)
  const [showHint, setShowHint] = useState(true)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<Array<HTMLElement | null>>([])

  // Group by version → progress bars and intra-group offset.
  const groups: { version: string; indices: number[] }[] = []
  milestones.forEach((m, i) => {
    let g = groups.find(g => g.version === m.when)
    if (!g) {
      g = { version: m.when, indices: [] }
      groups.push(g)
    }
    g.indices.push(i)
  })
  const activeGroupIdx = groups.findIndex(g => g.indices.includes(activeIdx))

  // IntersectionObserver per card with scroller as root.
  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller || typeof IntersectionObserver === 'undefined') return
    const observers: IntersectionObserver[] = []
    milestones.forEach((_, i) => {
      const el = cardRefs.current[i]
      if (!el) return
      const io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.55) {
            setActiveIdx(i)
          }
        },
        { root: scroller, threshold: [0.55, 0.75, 1] }
      )
      io.observe(el)
      observers.push(io)
    })
    return () => observers.forEach(io => io.disconnect())
  }, [milestones])

  // Hide hint after first scroll. Listener auto-removes (once: true).
  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return
    const onScroll = () => setShowHint(false)
    scroller.addEventListener('scroll', onScroll, { passive: true, once: true })
    return () => scroller.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className="pt-24 sm:pt-32 pb-20">
      <div className="px-6 md:px-16 mb-7">
        <div className="eyebrow mb-3">What's next</div>
        <h2
          id="roadmap-heading-mobile"
          className="font-display font-semibold text-ink-1 mb-7"
          style={{
            fontSize: 'clamp(44px, 12vw, 80px)',
            letterSpacing: '-0.04em',
            lineHeight: 1,
          }}
        >
          Roadmap
        </h2>

        {/* Stories progress bars — one per version */}
        <div className="flex items-end gap-1.5">
          {groups.map((g, gIdx) => {
            let fillRatio = 0
            if (gIdx < activeGroupIdx) fillRatio = 1
            else if (gIdx === activeGroupIdx) {
              const posInGroup = g.indices.indexOf(activeIdx)
              fillRatio = (posInGroup + 1) / g.indices.length
            }
            const isActive = gIdx === activeGroupIdx
            return (
              <div key={g.version} className="flex-1">
                <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full bg-[var(--accent)] rounded-full"
                    style={{
                      width: `${fillRatio * 100}%`,
                      transition: 'width 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
                    }}
                  />
                </div>
                <div
                  className="mt-2 text-center font-mono tracking-wider"
                  style={{
                    fontSize: '10px',
                    color: isActive ? 'var(--accent)' : '#737373',
                    transition: 'color 0.3s ease',
                  }}
                >
                  {g.version}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Scroll-snap viewport.
          touch-action: pan-x lets vertical pans fall through to the page so a
          downward swipe scrolls past the section instead of getting stuck on
          the horizontal track. overscroll-behavior-x: contain stops a swipe
          near the edges from triggering Safari's back/forward gesture. */}
      <div
        ref={scrollerRef}
        className="overflow-x-auto snap-x snap-mandatory no-scrollbar"
        style={{
          scrollBehavior: 'smooth',
          touchAction: 'pan-x',
          overscrollBehaviorX: 'contain',
        }}
      >
        <div className="flex">
          {milestones.map((m, i) => (
            <RoadmapStoryCard
              key={i}
              ref={el => (cardRefs.current[i] = el)}
              milestone={m}
              index={i}
              total={milestones.length}
            />
          ))}
        </div>
      </div>

      {/* Swipe hint — fades on first scroll */}
      <div
        className="flex items-center justify-center gap-2 mt-5 px-6"
        style={{
          opacity: showHint ? 1 : 0,
          transition: 'opacity 0.4s ease',
        }}
      >
        <span aria-hidden className="text-[12px] text-ink-3">←</span>
        <span className="text-[11px] text-ink-3 uppercase tracking-[2px]">swipe</span>
        <span aria-hidden className="text-[12px] text-ink-3">→</span>
      </div>
    </div>
  )
}

interface RoadmapStoryCardProps {
  milestone: Milestone
  index: number
  total: number
}

const RoadmapStoryCard = forwardRef<HTMLElement, RoadmapStoryCardProps>(function StoryCard(
  { milestone, index, total },
  ref
) {
  const badge = STATUS_BADGE[milestone.status]
  const { Icon } = milestone
  const articleRef = useRef<HTMLElement | null>(null)
  const iconRef = useRef<HTMLSpanElement>(null)

  // Icon spring + card fade-up when this card first enters viewport.
  useEffect(() => {
    const node = articleRef.current
    if (!node) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      node.setAttribute('data-visible', 'true')
      return
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          node.setAttribute('data-visible', 'true')
          iconRef.current?.classList.add('icon-spring-in')
          io.disconnect()
        }
      },
      { threshold: 0.2 }
    )
    io.observe(node)
    return () => io.disconnect()
  }, [])

  // Merge external ref + internal ref (we need both for IO + parent tracking).
  const setRef = (el: HTMLElement | null) => {
    articleRef.current = el
    if (typeof ref === 'function') ref(el)
    else if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = el
  }

  return (
    <article
      ref={setRef}
      className="reveal-up snap-center shrink-0 w-screen px-6"
    >
      <div className="glass p-6 min-h-[440px] flex flex-col">
        <div className="flex items-center gap-2 mb-7">
          <span
            className={`text-[11px] uppercase tracking-[2px] font-semibold px-2.5 py-1 rounded-full ${badge.className}`}
          >
            {badge.label}
          </span>
          <span className="text-[12px] text-ink-3 font-mono tracking-wider">
            {milestone.when}
          </span>
        </div>

        <div className="flex-1 flex flex-col">
          <span
            ref={iconRef}
            className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/[0.05] border border-white/10 mb-6"
            style={{ color: 'var(--accent)' }}
          >
            <Icon size={26} strokeWidth={1.75} />
          </span>
          <h3
            className="text-[28px] font-semibold tracking-tight text-ink-1 leading-tight mb-3"
            style={{ textWrap: 'balance' as never }}
          >
            {milestone.title}
          </h3>
          <p
            className="text-[15px] text-ink-2 leading-relaxed"
            style={{ textWrap: 'pretty' as never }}
          >
            {milestone.desc}
          </p>
        </div>

        <div className="mt-6 text-[11px] text-ink-3 font-mono tracking-widest">
          {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </div>
      </div>
    </article>
  )
})

function RoadmapCard({ milestone, index }: { milestone: Milestone; index: number }) {
  const badge = STATUS_BADGE[milestone.status]
  const { Icon } = milestone
  return (
    <article className="rm-card relative shrink-0 w-[300px] glass overflow-hidden p-5 flex flex-col">
      <span
        aria-hidden
        className="rm-fill pointer-events-none absolute inset-0 rounded-[inherit]"
        style={{
          background:
            'linear-gradient(135deg, var(--accent-soft) 0%, transparent 60%), radial-gradient(80% 100% at 0% 0%, var(--accent-soft) 0%, transparent 70%)',
          boxShadow:
            'inset 0 0 0 1px var(--accent-ring), 0 0 24px -4px var(--accent-ring)',
          opacity: 0,
        }}
      />

      <div className="rm-card-inner contents">
        <div className="flex items-center justify-between mb-4">
          <span
            className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-white/[0.05] border border-white/10"
            style={{ color: 'var(--accent)' }}
          >
            <Icon size={18} strokeWidth={1.75} />
          </span>
          <span
            className={`text-[10px] uppercase tracking-[2px] font-semibold px-2 py-0.5 rounded-full ${badge.className}`}
          >
            {badge.label}
          </span>
        </div>

        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[11px] font-mono text-ink-3 tracking-widest">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span className="text-ink-4">·</span>
          <span className="text-[11px] text-ink-3 tracking-wide">{milestone.when}</span>
        </div>

        <h3
          className="text-[20px] font-semibold tracking-tight text-ink-1 leading-tight mb-2"
          style={{ textWrap: 'balance' as never }}
        >
          {milestone.title}
        </h3>
        <p
          className="text-[13px] text-ink-2 leading-relaxed"
          style={{ textWrap: 'pretty' as never }}
        >
          {milestone.desc}
        </p>

        <div className="mt-4">
          <span
            className={`block w-2 h-2 rounded-full ${
              milestone.status === 'now'
                ? 'bg-[var(--accent)] shadow-[0_0_24px_var(--accent-ring)]'
                : 'bg-white/20'
            }`}
          />
        </div>
      </div>
    </article>
  )
}
